import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Team } from '@yandex-int/k-common/typings';
import { H3 } from 'platform-components';
import { InjectedProps as WithRouterPathProps, withRouterPath } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Action, bindActionCreators, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { getSelectedUmk } from 'common.components/umk/selectors';
import { getCurrentClass } from 'common.components/user/selectors';
import * as themesActions from 'store/themes/actions';
import * as themesSelectors from 'store/themes/selectors';

import SectionPageHeader from '../__header/section-page__header';
import { ReduxTeacherStore } from '../../../typings';
import SectionMenu from '../../library.section-menu/section-menu';
import * as libraryActions from '../../library.store/actions';

interface StoreProps {
    courseId: number | null;
    currentTeam: Team | null;
    currentTeamId: number | null;
    actualThemeDescription: string | null;
    actualThemesLoadingStatus: LOADING_STATUSES;
    shouldShowActualThemes: boolean;
    shouldLoadActualThemes: boolean;
    umk: string | null;
}

interface DispatchProps {
    getThemeInfoRequest: typeof libraryActions.getThemeDetailsRequest;
    loadActualThemesRequest: typeof themesActions.getActualThemesRequest;
}

interface HOCProps extends WithRouterPathProps {}

interface Props extends HOCProps, StoreProps, DispatchProps {}

interface Context {
    staticUrls: {
        labLibrarySubject: string;
    };
}

const SectionPageActual = (props: Props, context: Context) => {
    const {
        actualThemeDescription,
        shouldShowActualThemes,
        getRouterPath,
        actualThemesLoadingStatus,
        match: {
            params: { subject },
        },
        currentTeamId,
        shouldLoadActualThemes,
        currentTeam,
        umk,
        courseId,
        loadActualThemesRequest,
    } = props;
    const { staticUrls } = context;
    const { level: currentTeamLevel } = currentTeam || {};

    const loadActualThemes = useCallback(() => {
        if (courseId && currentTeamLevel) {
            loadActualThemesRequest({ courseId, grade: currentTeamLevel, umk, subject });
        }
    }, [loadActualThemesRequest, courseId, currentTeamLevel, umk, subject]);

    useEffect(() => {
        if (shouldLoadActualThemes) {
            loadActualThemes();
        }
    }, [loadActualThemes, subject, shouldLoadActualThemes, currentTeamId]);

    if (!shouldShowActualThemes || actualThemesLoadingStatus === LOADING_STATUSES.ERROR) {
        return <Redirect to={getRouterPath(staticUrls.labLibrarySubject)} />;
    }

    return (
        <React.Fragment>
            <SectionPageHeader description={actualThemeDescription} header={<H3 id="library.actualThemes" />} />
            <div className="section-page__section-menu">
                <SectionMenu isActualThemes />
            </div>
        </React.Fragment>
    );
};

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: WithRouterPathProps): StoreProps => {
    const {
        match: {
            params: { subject, classId },
        },
    } = ownProps;
    const courseId = getCurrentYearCourseId(storeState);
    const currentTeam = getCurrentClass(storeState, { classId });

    return {
        shouldLoadActualThemes: themesSelectors.getShouldLoadActualThemes(storeState, { classId, subject, courseId }),
        umk: getSelectedUmk(storeState, { classId, subject }),
        currentTeam,
        currentTeamId: currentTeam?.id || null,
        courseId,
        actualThemeDescription: themesSelectors.getActualThemesDescription(storeState, { classId, subject }),
        shouldShowActualThemes: themesSelectors.getShouldShowActualThemes(storeState, { classId, subject }),
        actualThemesLoadingStatus: themesSelectors.getActualThemesLoadingStatus(storeState, { subject, courseId }),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            getThemeInfoRequest: libraryActions.getThemeDetailsRequest,
            loadActualThemesRequest: themesActions.getActualThemesRequest,
        },
        dispatch
    );
};

SectionPageActual.contextTypes = {
    staticUrls: PropTypes.object.isRequired,
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(SectionPageActual));
